import React from "react";
import { NodeViewWrapper, NodeViewProps } from "@tiptap/react";
import { Box, Skeleton, Stack, Typography, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { PdfIcon } from "components/icons/PdfIcon";
import { CloseIcon, DownloadCurvedIcon } from "components/icons";

// A4 height at 96 DPI
const A4_HEIGHT = 842;

export const PdfEmbedNodeView: React.FC<NodeViewProps> = ({
  node,
  selected,
  editor,
  deleteNode,
}) => {
  const theme = useTheme();

  const { src, name, isLoading } = node.attrs;
  const isEditable = editor.isEditable;

  // Build PDF URL with parameters to hide sidebar and toolbar, fit to width
  const pdfUrl = src
    ? `${src}#navpanes=0&toolbar=0&scrollbar=1&view=FitH`
    : null;

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (src) {
      window.open(src, "_blank");
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteNode();
  };

  return (
    <NodeViewWrapper className="pdf-embed-wrapper">
      <Box
        sx={{
          width: "100%",
          my: "24px",
        }}
      >
        <Box
          sx={{
            border: selected
              ? `2px solid ${theme.palette.primary.main}`
              : `1px solid ${theme.palette.neutral["020"]}`,
            borderRadius: "8px",
            overflow: "hidden",
            transition: "border 0.15s ease",
          }}
        >
          {/* Header bar */}
          <Stack
            direction="row"
            sx={{
              height: "44px",
              backgroundColor: theme.palette.neutral["015"],
              alignItems: "center",
              justifyContent: "space-between",
              px: "12px",
              borderBottom: `1px solid ${theme.palette.neutral["020"]}`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap="8px"
              sx={{ overflow: "hidden", flex: 1 }}
            >
              <PdfIcon sx={{ width: 24, height: 24, flexShrink: 0 }} />
              <Typography
                variant="body-small"
                noWrap
                sx={{
                  color: theme.palette.text.primary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {name || "PDF Document"}
              </Typography>
            </Stack>

            <Stack direction="row" gap="4px" alignItems="center">
              {/* Download button */}
              {src && !isLoading && (
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  sx={{
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  <DownloadCurvedIcon fontSize="small" />
                </IconButton>
              )}

              {/* Delete button (if editable) */}
              {isEditable && (
                <IconButton
                  size="small"
                  onClick={handleDelete}
                  sx={{
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      color: theme.palette.error.main,
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Stack>

          {/* PDF content area */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: A4_HEIGHT,
              backgroundColor: theme.palette.neutral["010"],
            }}
          >
            {/* Loading overlay */}
            {isLoading && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  gap: "16px",
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width="90%"
                  height={300}
                  sx={{ borderRadius: "4px" }}
                />
                <Typography variant="body-small" color="text.secondary">
                  Uploading PDF...
                </Typography>
              </Box>
            )}

            {/* PDF iframe */}
            {src && !isLoading && (
              <iframe
                src={pdfUrl || ""}
                title={name || "PDF Document"}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </NodeViewWrapper>
  );
};
